import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { onSnapshot, query, collection, where, doc, updateDoc, writeBatch, orderBy, serverTimestamp, getDoc } from 'firebase/firestore';
import { db } from '@/firebase/config';

export function useUnreadMessages() {
  const [unreadCounts, setUnreadCounts] = useState<Record<string, number>>({});
  const [totalUnread, setTotalUnread] = useState(0);
  const { user } = useAuth();

  useEffect(() => {
    if (!user?.uid) return;

    const q = query(
      collection(db, 'conversations'),
      where('participantIds', 'array-contains', user.uid)
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const counts: Record<string, number> = {};
      let total = 0;

      snapshot.docs.forEach((doc) => {
        const unreadCount = doc.data().unreadCounts?.[user.uid] || 0;
        if (unreadCount > 0) {
          counts[doc.id] = unreadCount;
          total += unreadCount;
        }
      });

      setUnreadCounts(counts);
      setTotalUnread(total);
    });

    return () => unsubscribe();
  }, [user?.uid]);

  const clearUnreadCount = async (conversationId?: string) => {
    if (!user?.uid) return;

    try {
      if (conversationId) {
        const conversationRef = doc(db, 'conversations', conversationId);
        const convoDoc = await getDoc(conversationRef);
        
        if (!convoDoc.exists()) return;
        
        const currentData = convoDoc.data();
        if (
          currentData.participantIds.includes(user.uid) && 
          currentData.unreadCounts?.[user.uid] > 0
        ) {
          const updatedUnreadCounts = { ...currentData.unreadCounts };
          updatedUnreadCounts[user.uid] = 0;

          await updateDoc(conversationRef, {
            unreadCounts: updatedUnreadCounts
          });
        }
      } else {
        const batch = writeBatch(db);
        
        const conversationDocs = await Promise.all(
          Object.keys(unreadCounts).map(convId => 
            getDoc(doc(db, 'conversations', convId))
          )
        );
        
        conversationDocs.forEach(convoDoc => {
          if (convoDoc.exists() && user?.uid) {
            const currentData = convoDoc.data();
            if (
              currentData.participantIds.includes(user.uid) && 
              currentData.unreadCounts?.[user.uid] > 0
            ) {
              const updatedUnreadCounts = { ...currentData.unreadCounts };
              updatedUnreadCounts[user.uid] = 0;
              
              batch.update(convoDoc.ref, {
                unreadCounts: updatedUnreadCounts
              });
            }
          }
        });
        
        await batch.commit();
      }
    } catch (error) {
      console.error('Error clearing unread count:', error);
      throw error;
    }
  };

  return {
    unreadCounts,
    totalUnread,
    clearUnreadCount
  };
} 